import { transformCheckout, type TransformedCheckoutData } from "../transformers/CartResponseTransformer";
import type { AddressDTO, B2BUnitDataDTO, PaymentTypeDTO } from "../dto/CartResponseDTO";
import type { CheckoutViewDTO } from "../dto/CheckoutViewDTO";
import { buildEntryGroupsFromEntries } from "./preprocessCartEntries";

export async function getData(url: string): Promise<TransformedCheckoutData> {
  const response = await fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
  });
  const oldDto: CheckoutViewDTO = await response.json();
  const { dto, linearProducts } = preprocessData(oldDto);
  const checkoutData = transformCheckout(dto);
  if (linearProducts.length > 0) {
    checkoutData.products = [...checkoutData.products, ...linearProducts];
  }
  return checkoutData;
}

function preprocessData(dto: CheckoutViewDTO) {
  const orderables = buildEntryGroupsFromEntries(dto.orderables ?? [], "ORDERABLE", 1, 100);
  const quotables = buildEntryGroupsFromEntries(dto.quotables ?? [], "QUOTABLE", 2, 50_000);

  // Merge linear products from both streams, deduplicated by synthetic code
  const linearProductsByCode = new Map<string, (typeof orderables.linearProducts)[number]>();
  for (const lp of [...orderables.linearProducts, ...quotables.linearProducts]) {
    linearProductsByCode.set(lp.code, lp);
  }

  return {
    dto: {
      code: dto.code,
      affiliateComment: dto.affiliateComment,
      dstError: dto.dstError,
      b2bCustomerData: dto.b2bCustomerData,
      packageEntries: [...orderables.packageEntries, ...quotables.packageEntries],
      entryGroups: [...orderables.entryGroups, ...quotables.entryGroups],
      products: [
        ...orderables.products,
        // Merge quotable products, avoiding duplicates by productCode
        ...quotables.products.filter((p) => !orderables.products.some((op) => op.code === p.code)),
      ],

      // Checkout-only fields.
      // CheckoutViewDTO references the global AddressDTO (line1: string | null)
      // while CartResponseDTO.AddressDTO has line1?: string — structurally
      // compatible at runtime; cast to satisfy the stricter DTO contract.
      purchaseOrderNr: dto.purchaseOrderNr,
      deliveryInstruction: dto.deliveryInstruction,
      shipTo: dto.shipTo as unknown as B2BUnitDataDTO,
      soldTo: dto.soldTo as unknown as B2BUnitDataDTO,
      deliveryAddressesList: dto.deliveryAddressesList as unknown as AddressDTO[] | null,
      defaultAddress: dto.defaultAddress as unknown as AddressDTO,
      deliveryAddress: dto.deliveryAddress as unknown as AddressDTO | undefined,
      countriesList: dto.countriesList,
      unit: dto.unit as Record<string, never>,
      canAddAddress: dto.canAddAddress,
      defaultCreditCard: dto.defaultCreditCard,
      supportedPaymentTypes: dto.supportedPaymentTypes as unknown as PaymentTypeDTO[],
    },
    linearProducts: Array.from(linearProductsByCode.values()),
  };
}
